import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiSubmitContact, formatApiError } from '../lib/api';
import { useSettings } from '../contexts/SettingsContext';

export const Contact = () => {
  const { settings } = useSettings();
  const s = settings || {};
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'brand',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiSubmitContact(formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', type: 'brand', message: '' });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-gray-800 text-white relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-600 rounded-full mix-blend-overlay filter blur-3xl opacity-25"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-rose-600 rounded-full mix-blend-overlay filter blur-3xl opacity-25"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT — Editorial copy + socials */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <p className="text-sm font-bold tracking-[0.2em] text-orange-400 uppercase mb-4">
                {s.contact_eyebrow || "Let's talk"}
              </p>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
                {s.contact_headline_line1 || 'Got a brand?'}
                <br />
                {s.contact_headline_line2 || 'Got a story?'}
                <br />
                <span className="italic font-serif text-orange-400">{s.contact_headline_italic || "We've got the rest."}</span>
              </h2>
            </div>

            <p className="text-lg text-gray-300 leading-relaxed">
              {s.contact_description || "Whether you're a brand chasing reach or a creator chasing growth — drop us a line. We respond within 24 hours."}
            </p>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-10 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                {/* Type toggle */}
                <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-full">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'brand' })}
                    className={`py-2.5 rounded-full text-sm font-semibold transition-all ${
                      formData.type === 'brand'
                        ? 'bg-gray-900 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="contact-type-brand"
                  >
                    I'm a brand
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'creator' })}
                    className={`py-2.5 rounded-full text-sm font-semibold transition-all ${
                      formData.type === 'creator'
                        ? 'bg-gray-900 text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="contact-type-creator"
                  >
                    I'm a creator
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      data-testid="contact-form-name"
                      className="h-12 border-gray-200 focus:border-gray-900 focus:ring-0 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      required
                      data-testid="contact-form-email"
                      className="h-12 border-gray-200 focus:border-gray-900 focus:ring-0 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={
                      formData.type === 'brand'
                        ? 'Tell us about your brand, campaign goals, and budget...'
                        : 'Tell us about your niche, followers, and content style...'
                    }
                    required
                    rows={5}
                    data-testid="contact-form-message"
                    className="border-gray-200 focus:border-gray-900 focus:ring-0 rounded-xl resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-base font-semibold rounded-xl transition-all duration-300 hover:scale-[1.01] group"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send message
                      <Send size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center pt-2">
                  We typically respond within 24 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
